(function() {
  'use strict';

  // Cache configuration
  const GEOCODE_CACHE_KEY = 'pickup_availability_geocode_cache';
  const USER_POSITION_CACHE_KEY = 'pickup_availability_user_position';
  const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
  const USER_POSITION_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get geocoded addresses cache from sessionStorage
   * Automatically removes expired entries (older than 24 hours)
   * @returns {Object} Cache object with address as key and {lat, lon, timestamp} as value
   */
  function getGeocodeCache() {
    try {
      const cached = sessionStorage.getItem(GEOCODE_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        const valid = {};
        for (const [address, data] of Object.entries(parsed)) {
          if (data.timestamp && (now - data.timestamp) < CACHE_DURATION) {
            valid[address] = data;
          }
        }
        return valid;
      }
    } catch (e) {
      console.error('Erreur lors de la lecture du cache de géocodage:', e);
    }
    return {};
  }

  /**
   * Store geocoded address coordinates in sessionStorage cache
   * @param {string} address - The normalized address
   * @param {Object} coords - Object with lat and lon properties
   */
  function setGeocodeCache(address, coords) {
    try {
      const cache = getGeocodeCache();
      cache[address] = {
        lat: coords.lat,
        lon: coords.lon,
        timestamp: Date.now()
      };
      sessionStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.error('Erreur lors de l\'écriture du cache de géocodage:', e);
    }
  }

  /**
   * Get cached user position from sessionStorage
   * Returns null if cache is expired (older than 5 minutes) or doesn't exist
   * @returns {Object|null} Object with lat and lon properties, or null
   */
  function getUserPositionCache() {
    try {
      const cached = sessionStorage.getItem(USER_POSITION_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        const now = Date.now();
        if (parsed.timestamp && (now - parsed.timestamp) < USER_POSITION_CACHE_DURATION) {
          return { lat: parsed.lat, lon: parsed.lon };
        }
      }
    } catch (e) {
      console.error('Erreur lors de la lecture du cache de position:', e);
    }
    return null;
  }

  /**
   * Store user position in sessionStorage cache
   * @param {number} lat - Latitude
   * @param {number} lon - Longitude
   */
  function setUserPositionCache(lat, lon) {
    try {
      sessionStorage.setItem(USER_POSITION_CACHE_KEY, JSON.stringify({
        lat,
        lon,
        timestamp: Date.now()
      }));
    } catch (e) {
      console.error('Erreur lors de l\'écriture du cache de position:', e);
    }
  }

  /**
   * Clean address string for cache key consistency
   * Removes extra spaces, normalizes commas
   * @param {string} address - Raw address string
   * @returns {string} Cleaned address string
   */
  function cleanAddressForCache(address) {
    if (!address) return '';
    return address.trim()
      .replace(/\s+/g, ' ')
      .replace(/\s*,\s*/g, ', ')
      .replace(/,\s*,/g, ',')
      .replace(/^,\s*/, '')
      .replace(/\s*,$/, '')
      .trim();
  }

  /**
   * Initialize pickup availability sorting functionality
   * Handles geolocation-based sorting and alphabetical fallback
   */
  function initPickupAvailabilitySort() {
    const container = document.querySelector('.pickup-availability.pk-pickup-availability');
    if (!container) return;

    // Disable geolocation in theme editor (permissions policy blocks it)
    const isThemeEditor = typeof Shopify !== 'undefined' && Shopify.designMode;
    
    // Check if geolocation is enabled via data attribute (from theme settings)
    const enableGeolocation = !isThemeEditor && container.dataset.enableGeolocation !== 'false';
    
    const storesContainer = container.querySelector('[data-stores-container]');
    const drawerContainer = container.querySelector('[data-stores-drawer-container]');
    
    if (!storesContainer && !drawerContainer) return;

    /**
     * Get current store items from DOM
     * @returns {Array<HTMLElement>} Array of store item elements
     */
    function getCurrentStoreItems() {
      return Array.from(container.querySelectorAll('.pk-store-item, .pk-store-drawer-item'));
    }

    /**
     * Calculate distance between two coordinates using Haversine formula
     * @param {number} lat1 - First point latitude
     * @param {number} lon1 - First point longitude
     * @param {number} lat2 - Second point latitude
     * @param {number} lon2 - Second point longitude
     * @returns {number} Distance in kilometers
     */
    function calculateDistance(lat1, lon1, lat2, lon2) {
      const R = 6371; // Earth radius in km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c;
    }

    /**
     * Normalize address format for geocoding
     * Fixes common formatting issues like "Place du 75001 LouvreParis" -> "Place du Louvre, 75001 Paris"
     * @param {string} address - Raw address string
     * @returns {string} Normalized address string
     */
    function normalizeAddress(address) {
      let clean = cleanAddressForCache(address);
      
      // Pattern 1: Fix "street 75001 cityPart1cityPart2" format
      // Example: "Place du 75001 LouvreParis" -> "Place du Louvre, 75001 Paris"
      clean = clean.replace(/([A-Za-zÀ-ÿ\s]+?)\s+(\d{5})\s+([A-Za-zÀ-ÿ]+?)([A-Za-zÀ-ÿ]+?)(,|$)/g, (match, street, zip, cityPart1, cityPart2, comma) => {
        // If cityPart1 is very short (1-2 chars), it's likely the end of street name
        if (cityPart1.length <= 2 && street.trim().split(' ').length > 2) {
          const lastWord = street.trim().split(' ').pop();
          const streetWithoutLast = street.trim().replace(/\s+\w+$/, '');
          const city = lastWord + cityPart1 + cityPart2;
          return `${streetWithoutLast} ${lastWord}, ${zip} ${city}${comma}`;
        } else {
          const city = cityPart1 + cityPart2;
          return `${street.trim()}, ${zip} ${city}${comma}`;
        }
      });
      
      // Pattern 2: Fix postal code attached to city name
      // Example: "Paris75001" -> "75001 Paris" or "75001Paris" -> "75001 Paris"
      clean = clean.replace(/([A-Za-zÀ-ÿ]+)(\d{5})/g, '$2 $1');
      clean = clean.replace(/(\d{5})([A-Za-zÀ-ÿ]+)/g, '$1 $2');
      clean = clean.replace(/\s+/g, ' ').replace(/,\s*,/g, ',').trim();
      
      return clean;
    }

    /**
     * Geocode an address using OpenStreetMap Nominatim API
     * Uses cache to avoid redundant API calls
     * @param {string} address - Store address
     * @param {string} storeName - Store name (for error logging)
     * @returns {Promise<Object|null>} Object with lat and lon, or null if geocoding fails
     */
    async function geocodeAddress(address, storeName = '') {
      if (!address || address.trim() === '') {
        return null;
      }
      
      const cleanAddress = normalizeAddress(address);
      const cache = getGeocodeCache();
      
      // Return cached coordinates if available
      if (cache[cleanAddress]) {
        return {
          lat: cache[cleanAddress].lat,
          lon: cache[cleanAddress].lon
        };
      }
      
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cleanAddress)}&limit=1&addressdetails=1`;
        
        const response = await fetch(url, {
          headers: {
            'User-Agent': 'Shopify-Theme/1.0',
            'Accept-Language': 'fr-FR,fr;q=0.9'
          }
        });
        
        if (!response.ok) {
          return null;
        }
        
        const data = await response.json();
        
        if (data && data.length > 0) {
          const result = {
            lat: parseFloat(data[0].lat),
            lon: parseFloat(data[0].lon)
          };
          
          // Cache the result for future use
          setGeocodeCache(cleanAddress, result);
          return result;
        }
      } catch (error) {
        console.error('Erreur de géocodage:', error);
      }
      
      return null;
    }

    /**
     * Sort items alphabetically by store name
     * @param {Array<HTMLElement>} items - Store item elements
     * @returns {Array<HTMLElement>} Sorted items
     */
    function sortAlphabetically(items) {
      return items.sort((a, b) => {
        const nameA = a.dataset.storeName || '';
        const nameB = b.dataset.storeName || '';
        return nameA.localeCompare(nameB, 'fr');
      });
    }

    /**
     * Sort items by availability first, then by custom sort function
     * Available stores come first, then unavailable stores
     * @param {Array<HTMLElement>} items - Store item elements
     * @param {Function} sortFn - Sort function to apply within each group
     * @returns {Array<HTMLElement>} Sorted items
     */
    function sortByAvailability(items, sortFn) {
      const available = items.filter(item => item.dataset.storeAvailable === 'true');
      const unavailable = items.filter(item => item.dataset.storeAvailable === 'false');
      
      return [...sortFn(available), ...sortFn(unavailable)];
    }

    /**
     * Apply sorted items to DOM
     * Reorders elements in both main container and drawer
     * @param {Array<HTMLElement>} sortedItems - Sorted store item elements
     */
    function applySort(sortedItems) {
      if (storesContainer) {
        const mainItems = sortedItems.filter(item => item.classList.contains('pk-store-item'));
        mainItems.forEach(item => storesContainer.appendChild(item));
      }
      
      if (drawerContainer) {
        const drawerItems = sortedItems.filter(item => item.classList.contains('pk-store-drawer-item'));
        drawerItems.forEach(item => drawerContainer.appendChild(item));
      }
    }

    /**
     * Calculate distances and sort stores by proximity
     * @param {number} userLat - User latitude
     * @param {number} userLon - User longitude
     * @param {Array<HTMLElement>} currentItems - Store item elements
     * @param {Array<HTMLElement>} sortedAlphabetically - Alphabetically sorted items (fallback)
     */
    async function calculateAndSortDistances(userLat, userLon, currentItems, sortedAlphabetically) {
      const storesWithDistance = [];
      const cache = getGeocodeCache();
      
      for (let i = 0; i < currentItems.length; i++) {
        const item = currentItems[i];
        const address = item.dataset.storeAddress || '';
        const coords = await geocodeAddress(address);
        
        if (coords && coords.lat && coords.lon) {
          const distance = calculateDistance(userLat, userLon, coords.lat, coords.lon);
          storesWithDistance.push({ item, distance });
        } else {
          // If geocoding fails, use Infinity distance (will be sorted last)
          storesWithDistance.push({ item, distance: Infinity });
        }
        
        // Rate limiting: 1 second delay between API calls (only if not cached)
        const cleanAddress = cleanAddressForCache(address);
        if (!cache[cleanAddress] && i < currentItems.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      // Sort by availability first, then by distance
      const sortedByDistance = sortByAvailability(
        storesWithDistance.map(s => s.item),
        (items) => {
          return items.sort((a, b) => {
            const distA = storesWithDistance.find(s => s.item === a)?.distance ?? Infinity;
            const distB = storesWithDistance.find(s => s.item === b)?.distance ?? Infinity;
            return distA - distB;
          });
        }
      );

      applySort(sortedByDistance);
    }

    /**
     * Main sorting function
     * Handles geolocation request and sorting logic
     * @param {boolean} forceGeolocation - Force new geolocation request (ignore cache)
     */
    async function sortStores(forceGeolocation = false) {
      const currentItems = getCurrentStoreItems();
      if (currentItems.length === 0) return;

      // Always prepare alphabetical sort as fallback
      const sortedAlphabetically = sortByAvailability(currentItems, sortAlphabetically);
      
      // If geolocation is disabled via settings, use alphabetical sort only
      if (!enableGeolocation) {
        applySort(sortedAlphabetically);
        return;
      }
      
      // Check if geolocation API is available
      let geolocationAvailable = false;
      
      try {
        if (typeof navigator !== 'undefined' && navigator.geolocation) {
          geolocationAvailable = true;
        }
      } catch (e) {
        geolocationAvailable = false;
      }

      if (!geolocationAvailable) {
        applySort(sortedAlphabetically);
        return;
      }

      // Additional check: verify geolocation permissions are not blocked
      // This prevents errors in theme editor where permissions policy blocks geolocation
      if (typeof navigator.permissions !== 'undefined') {
        try {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          if (permission.state === 'denied') {
            applySort(sortedAlphabetically);
            return;
          }
        } catch (e) {
          // Permissions API not supported or blocked, continue anyway
        }
      }

      // Check cached user position (5 minutes cache)
      const cachedPosition = getUserPositionCache();
      if (cachedPosition && !forceGeolocation) {
        await calculateAndSortDistances(cachedPosition.lat, cachedPosition.lon, currentItems, sortedAlphabetically);
        return;
      }

      // Request geolocation from browser
      try {
        const geolocationOptions = { 
          timeout: 10000,
          enableHighAccuracy: false,
          maximumAge: forceGeolocation ? 0 : 300000 // 5 minutes cache if not forced
        };

        navigator.geolocation.getCurrentPosition(
          async (position) => {
            const userLat = position.coords.latitude;
            const userLon = position.coords.longitude;
            
            // Cache user position for future use
            setUserPositionCache(userLat, userLon);
            await calculateAndSortDistances(userLat, userLon, currentItems, sortedAlphabetically);
          },
          (error) => {
            // Geolocation failed, denied, or blocked by permissions policy
            // Silently fallback to alphabetical sort (no console.error to avoid spam in theme editor)
            applySort(sortedAlphabetically);
          },
          geolocationOptions
        );
      } catch (error) {
        // Catch permissions policy violations and other errors
        // Silently fallback to alphabetical sort
        applySort(sortedAlphabetically);
      }
    }

    // Find drawer and button elements
    const drawer = container.querySelector('x-drawer');
    const drawerButton = container.querySelector('button[aria-controls]');

    // Request geolocation when drawer button is clicked (user interaction)
    if (drawerButton && drawer) {
      drawerButton.addEventListener('click', () => {
        setTimeout(() => {
          sortStores(true); // Force new geolocation request
        }, 300);
      });
    }

    // Also request geolocation when drawer opens
    if (drawer) {
      drawer.addEventListener('dialog:after-show', () => {
        sortStores(true); // Force new geolocation request
      });
    }

    // Initial sort on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => sortStores(false));
    } else {
      sortStores(false);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPickupAvailabilitySort);
  } else {
    initPickupAvailabilitySort();
  }
})();
